import React, { useState, useEffect, useRef } from "react";
import { syncCurso } from "../../../../services/contificoService";
import { showToast } from "../../../../utils/showToast";

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return "-";
    return new Intl.NumberFormat("es-EC", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    }).format(num);
};

export default function CursosTable({
    cursos,
    loading,
    error,
    reload,
    onEdit,
    onDelete,
    onSynced,
    canEdit = true,
    canDelete = true,
    showSync = false,
    maxHeight = "60vh",
}) {
    const minRows = 10;
    const [openMenuId, setOpenMenuId] = useState(null);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
    const [syncingId, setSyncingId] = useState(null);
    const wrapRef = useRef(null);

    const handleSync = async (e, curso) => {
        e.stopPropagation();
        setOpenMenuId(null);
        setSyncingId(curso.id);
        try {
            const res = await syncCurso(curso.id);
            showToast({ type: "success", message: `Curso ${res.accion === "vinculado_desde_catalogo" ? "vinculado" : res.accion} en Contífico.` });
            onSynced?.(curso, res);
        } catch (err) {
            showToast({ type: "danger", message: err.message || "Error al sincronizar con Contífico." });
        } finally {
            setSyncingId(null);
        }
    };

    useEffect(() => {
        const handleOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    if (loading) {
        return (
            <div className="management-card text-center py-5">
                <div className="spinner-border text-primary" role="status" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="management-card management-alert d-flex justify-content-between align-items-center">
                <span>{error}</span>
                {reload && (
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload}>
                        Reintentar
                    </button>
                )}
            </div>
        );
    }

    if (!cursos.length) {
        return (
            <div className="management-card curso-empty text-center py-5">
                <i className="bi bi-hdd-stack curso-empty-icon mb-3" />
                <h5 className="fw-semibold mb-1">No hay cursos registrados.</h5>
                <p className="text-muted mb-0">Crea un curso o ajusta los filtros.</p>
            </div>
        );
    }

    const emptyRows = Math.max(0, minRows - cursos.length);
    const hasActions = canEdit || canDelete || showSync;

    return (
        <div className="management-card curso-table-card">
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div>
                    <p className="management-label mb-1">Listado</p>
                    <h6 className="mb-0">Cursos registrados</h6>
                </div>
                {reload && (
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload} disabled={loading}>
                        <i className="bi bi-arrow-repeat me-2" />
                        Actualizar
                    </button>
                )}
            </div>

            <div className="management-table-wrapper" style={{ maxHeight }}>
                <table className="table align-middle management-table curso-table mb-0">
                    <thead className="bg-transparent">
                        <tr>
                            <th>#</th>
                            <th>Codigo</th>
                            <th>Curso / Centro</th>
                            <th>Nivel y especialidad</th>
                            <th>Precio base</th>
                            <th>Reserva / Matrícula</th>
                            <th className="text-center">Estado</th>
                            {hasActions && <th className="text-end">Acciones</th>}
                        </tr>
                    </thead>
                    <tbody ref={wrapRef}>
                        {cursos.map((curso, index) => {
                            const key = curso.id !== undefined ? `curso-${curso.id}` : `curso-${index}`;
                            const isMenuOpen = openMenuId === curso.id;
                            return (
                                <tr key={key}>
                                    <td className="fw-semibold">{index + 1}</td>
                                    <td>
                                        <span className="badge bg-light text-dark">{curso.codigoCurso || "-"}</span>
                                    </td>
                                    <td>
                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                            {curso.nombre || "Sin nombre"}
                                            {showSync && (
                                                syncingId === curso.id
                                                    ? <span className="spinner-border spinner-border-sm text-primary" />
                                                    : curso.contificoProductoId
                                                        ? <i className="bi bi-cloud-check-fill text-success" title="Sincronizado con Contífico" style={{ fontSize: 12 }} />
                                                        : <i className="bi bi-cloud-slash text-muted" title="Sin sync" style={{ fontSize: 12 }} />
                                            )}
                                        </div>
                                        <small className="text-muted">{curso.centroNombre || "Sin centro"}</small>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-column gap-1">
                                            <span className="badge bg-light text-dark">
                                                <i className="bi bi-layers me-1" />
                                                {curso.nivelNombre || "Sin nivel"}
                                            </span>
                                            <span className="badge bg-white border text-dark">
                                                <i className="bi bi-diagram-3 me-1" />
                                                {curso.especialidadNombre || "Sin especialidad"}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="fw-semibold">{formatCurrency(curso.precioBase)}</td>
                                    <td>
                                        <div className="d-flex flex-column">
                                            <span className="badge bg-light text-dark">
                                                Reserva: {formatCurrency(curso.montoMinReserva)}
                                            </span>
                                            <span className="badge bg-white border text-dark">
                                                Matrícula: {formatCurrency(curso.montoMatricula)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <span
                                            className={`badge rounded-pill px-3 py-2 ${curso.activo ? "bg-success-subtle text-success" : "bg-secondary-subtle text-muted"}`}
                                        >
                                            {curso.activo ? "Activo" : "Inactivo"}
                                        </span>
                                    </td>
                                    {hasActions && (
                                        <td className="text-end">
                                            <div className="management-actions-wrap">
                                                <button
                                                    type="button"
                                                    className={`management-actions-toggle${isMenuOpen ? " is-open" : ""}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!isMenuOpen) {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                                        }
                                                        setOpenMenuId(isMenuOpen ? null : curso.id);
                                                    }}
                                                    title="Acciones"
                                                    aria-expanded={isMenuOpen}
                                                >
                                                    <i className="bi bi-three-dots-vertical" />
                                                </button>
                                                {isMenuOpen && (
                                                    <ul className="management-actions-menu" style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}>
                                                        {canEdit && (
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="menu-item"
                                                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onEdit?.(curso); }}
                                                                >
                                                                    <i className="bi bi-pencil-square" />
                                                                    Editar
                                                                </button>
                                                            </li>
                                                        )}
                                                        {canDelete && (
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="menu-item is-danger"
                                                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); onDelete?.(curso); }}
                                                                >
                                                                    <i className="bi bi-trash" />
                                                                    Eliminar
                                                                </button>
                                                            </li>
                                                        )}
                                                        {showSync && (
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="menu-item"
                                                                    onClick={(e) => handleSync(e, curso)}
                                                                    disabled={syncingId === curso.id}
                                                                >
                                                                    <i className="bi bi-cloud-upload" />
                                                                    {curso.contificoProductoId ? "Re-sincronizar" : "Sincronizar Contífico"}
                                                                </button>
                                                            </li>
                                                        )}
                                                    </ul>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {emptyRows > 0 &&
                            Array.from({ length: emptyRows }).map((_, index) => (
                                <tr key={`curso-empty-${index}`} className="table-empty-row" aria-hidden="true">
                                    <td colSpan={hasActions ? 8 : 7}>&nbsp;</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
